import { Box, Stack, useTheme } from '@mui/material';
import { forwardRef } from 'react';

import {
  Logomark,
  LogomarkSize,
  LogomarkVariant,
} from '@/components/brand/Logomark/Logomark';

import { PresentationCoverSlideConfig } from '../coverSlide/CoverSlide.types';
import { SLIDE_CONTENT_TOP_OFFSET_SPACING } from './slideContentSpacing';

/**
 * Drag/scale values pulled off the merged tenant slide design with the
 * "null/undefined → sensible default" defaults applied. Shared by
 * `CoverSlide` and `DividerSlide` so the two slides stay in lockstep.
 */
export interface SlideLogoState {
  /** logoPercentScale as a CSS scale factor (1 == 100%). */
  logoScaleFactor: number;
  /** Whether the logo is anchored to the title block (default) or
   *  rendered independently via drag offsets. */
  useDefaultLogoPosition: boolean;
  /** Drag-driven logo offsets in slide-coordinate pixels. */
  logoOriginX: number;
  logoOriginY: number;
  /** Drag-driven title-block offsets in slide-coordinate pixels. */
  textOriginX: number;
  textOriginY: number;
}

export function getSlideLogoState(
  tenantSlideDesign?: PresentationCoverSlideConfig
): SlideLogoState {
  return {
    logoScaleFactor: (tenantSlideDesign?.logoPercentScale ?? 100) / 100,
    useDefaultLogoPosition: tenantSlideDesign?.useDefaultLogoPosition ?? true,
    logoOriginX: tenantSlideDesign?.logoOriginX ?? 0,
    logoOriginY: tenantSlideDesign?.logoOriginY ?? 0,
    textOriginX: tenantSlideDesign?.textOriginX ?? 0,
    textOriginY: tenantSlideDesign?.textOriginY ?? 0,
  };
}

interface SlideLogomarkProps {
  useDarkLogo?: boolean | null;
  /** scale factor — typically read off `getSlideLogoState`. */
  scaleFactor: number;
  /**
   * When false, the scale transform is skipped and the caller is
   * expected to apply the scale on an outer wrapper. Used by
   * `CustomPositionedSlideLogo` and the inline logo wrapper in
   * `CoverSlide`/`DividerSlide` so the wrapper's transformed bounds
   * match the visible image (lets the admin hover halo wrap the
   * post-scale bounds rather than the unscaled layout box).
   */
  applyScale?: boolean;
  /**
   * Clamp the img to `maxHeight: 15vh`. Enabled on the inline (default-
   * position) logo render path so square/tall logos can't tower over
   * the title block. The custom-positioned render path leaves this
   * off — the admin has explicitly set `logoPercentScale` and dragged
   * the logo into place, so a hard ceiling would override that intent.
   */
  enforceMaxHeight?: boolean;
}

/**
 * The tenant Logomark as rendered on cover/divider slides — bounded to
 * the natural ~15vh × 640px box and scaled via CSS transform so the
 * scale never triggers layout reflow (the title baseline stays put as
 * the logo grows/shrinks).
 */
export function SlideLogomark({
  useDarkLogo,
  scaleFactor,
  applyScale = true,
  enforceMaxHeight = false,
}: SlideLogomarkProps) {
  return (
    <Stack direction="row" alignItems="center" sx={{ maxHeight: '15vh' }}>
      <Logomark
        imageSx={{
          height: '100%',
          maxWidth: '640px',
          ...(enforceMaxHeight && { maxHeight: '15vh' }),
          ...(applyScale && {
            transform: `scale(${scaleFactor})`,
            transformOrigin: 'left bottom',
          }),
        }}
        size={LogomarkSize.Wide}
        variant={
          useDarkLogo ? LogomarkVariant.Secondary : LogomarkVariant.Primary
        }
      />
    </Stack>
  );
}

interface CustomPositionedSlideLogoProps {
  useDarkLogo?: boolean | null;
  /**
   * Slide name used for the `data-name` attribute so the rendered DOM
   * tells you which slide the layer belongs to (handy in devtools).
   */
  slideName: 'CoverSlide' | 'DividerSlide';
  /** scale factor — same source as the inline Logomark. */
  scaleFactor: number;
  /** Drag-driven X offset in slide-coordinate pixels. */
  logoOriginX: number;
  /** Drag-driven Y offset in slide-coordinate pixels. */
  logoOriginY: number;
}

/**
 * Absolute-positioned logo rendered behind the title block when the
 * admin has opted out of the default position. The base anchor matches
 * the title block's natural top-left so toggling the checkbox is
 * visually continuous; drag offsets apply via `transform: translate`
 * so they stay GPU-composited. The title block carries an explicit
 * higher zIndex so cover/divider text is never occluded by the logo
 * when the two overlap (T1-5979).
 */
export const CustomPositionedSlideLogo = forwardRef<
  HTMLDivElement,
  CustomPositionedSlideLogoProps
>(function CustomPositionedSlideLogo(
  { slideName, scaleFactor, logoOriginX, logoOriginY, useDarkLogo },
  ref
) {
  const theme = useTheme();
  return (
    <Box
      ref={ref}
      data-name={`${slideName}.CustomLogo`}
      sx={{
        position: 'absolute',
        top: theme.spacing(SLIDE_CONTENT_TOP_OFFSET_SPACING),
        left: theme.spacing(10.5),
        // Combine the drag translate with the scale so the outer
        // box's post-transform bounds match the visible image. Outline
        // is painted after transforms, so the admin hover halo on this
        // element wraps the scaled image instead of the unscaled
        // layout box. `SlideLogomark` is rendered with `applyScale={false}`
        // to avoid double-scaling. Layout impact stays zero — the box
        // is absolute-positioned, so even a >1 scale doesn't push
        // surrounding content.
        transform: `translate(${logoOriginX}px, ${logoOriginY}px) scale(${scaleFactor})`,
        transformOrigin: 'left bottom',
      }}
    >
      <SlideLogomark
        scaleFactor={scaleFactor}
        applyScale={false}
        useDarkLogo={useDarkLogo}
      />
    </Box>
  );
});
